import styled from "@emotion/styled/macro";
import { useCallback, useEffect, useState } from "react";
import { uniqBy } from "lodash";
import {
  LoadingOverlay,
  Modal,
  tokens,
} from "@sunrun/experience-ui-components";
import { getEnvironment } from "@sunrun/sales-experience-shared";
import camelCase from "lodash/camelCase";
import { CamelCase } from "type-fest";
import { UserProfile } from "@sunrun/sales-experience-shared";
import type { PricingOffer } from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { PricingPayment } from "components/organisms/PricingPayment";
import { PricingOptions } from "components/organisms/PricingOptions";
import {
  financialProductsOrderedList,
  getDefaultFinancialProduct,
  getPricingFields,
  PRICING_UNAVAILABLE_MESSAGE,
} from "providers/pricing/getPricingFields";
import {
  ActiveFilters,
  Filters,
  FilterType,
  Group,
  Payload,
} from "providers/pricing/fields/Filters";
import {
  getPricePoints,
  getPricingOffer,
  onDomainEvent,
  submitOffer,
  UserRole,
} from "services/storeFront";
import { getUserRole } from "services/userPermissions";

import type {
  FinancialProduct,
  PricePoint,
  UnifiedPricingData,
} from "providers/pricing/PricePoints";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { rollbar } from "providers/rollbar";
import { transferDesignImages } from "services/lightmileProject";
import { useAuthToken } from "hooks/useAuthToken";
import { getQueryStringParams } from "utils/queryString";
import { CurrentContractTerms } from "components/organisms/CurrentContractTerms";
import { CurrentHomeUpgradeSection } from "components/organisms/CurrentHomeUpgradeSection";
import { useAppSelector } from "store";
import { OFFER_CREATION__SUBMIT_OFFER } from "constants/mixpanelEvents";
import { Mixpanel } from "providers/mixpanel";
import { useUserProfile } from "cap/hooks/useCapSwr";
import { FUSION, SalesDivision } from "fixtures/salesDivision";
import { shouldHideSolarRateColumn } from "utils/hideSolarRateColumn";
import { getStoreFrontDesignImagesWithAuthRetry } from "services/designImagesAuth";

const pricePointsFilter = (
  name: string,
  pricePoints: PricePoint[],
  activeFilters: ActiveFilters,
  filters: Filters
) => {
  const activeFiltersWithDefaults = uniqBy(
    filters.reduce((activeAndDefaultFilters, filter) => {
      if (filter.type === FilterType.Dropdown) {
        return [
          ...activeAndDefaultFilters,
          {
            filter,
            payload: filter.defaultPayload(
              filter.payloads(
                pricePoints,
                filter.value as any,
                filter.displayFilter
              )
            ),
          },
        ];
      }
      return activeAndDefaultFilters;
    }, activeFilters as ActiveFilters),
    "filter"
  );
  const results = pricePoints.reduce((pricePoints, pricePoint) => {
    const passed = activeFiltersWithDefaults.reduce(
      (pass: boolean, activeFilter) => {
        switch (activeFilter.filter.type) {
          case FilterType.Dropdown:
            return (
              pass &&
              activeFilter.filter.value(pricePoint) ===
                activeFilter.payload.value
            );
          case FilterType.MinMaxSlider:
            const value = activeFilter.filter.value(pricePoint) as any;
            return (
              pass &&
              value >= activeFilter.payload.min &&
              value <= activeFilter.payload.max
            );
          default:
            return pass;
        }
      },
      true
    );

    if (passed) return [...pricePoints, pricePoint];
    return pricePoints;
  }, [] as PricePoint[]);
  return results;
};

type PricingPageProps = {
  lightmileProjectId?: string;
  offerId: string;
  prospectId: string;
  changeOrder: boolean;
  offerEvSpan: boolean;
  submittingOffer: boolean;
  onSubmitFailure: (err: Error) => void;
  onSubmitComplete: () => void;
  onPricePointIdSelected: (pricePointId: string) => void;
  repChangeOrder?: boolean;
  setChangeOrderError?: (err: Error) => void;
};

const PricingPage = ({
  lightmileProjectId,
  offerId,
  changeOrder,
  offerEvSpan,
  submittingOffer,
  onSubmitFailure,
  onSubmitComplete,
  onPricePointIdSelected,
  repChangeOrder = false,
  setChangeOrderError,
}: PricingPageProps) => {
  const [hideSolarRateColumn, setHideSolarRateColumn] = useState(false);
  const [errorData, setErrorData] = useState<{
    error: Error;
    action: string;
    severity?: "error" | "warning";
  }>();

  const reportPricingUnavailable = () => {
    const error = new Error(PRICING_UNAVAILABLE_MESSAGE);
    setErrorData({ error, action: "Get Pricing", severity: "warning" });
    rollbar.warning(error, { context: "PricingPage", action: "Get Pricing" });
  };

  const ihdToken = useAuthToken("IHD");
  const hybridToken = useAuthToken("HYBRID");
  const proposalToken = useAuthToken("PROPOSAL");
  const authToken = ihdToken ?? hybridToken ?? proposalToken;
  const [userRole, setUserRole] = useState<UserRole>();
  const hasHybridAuth = Boolean(hybridToken?.token);

  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );

  const { userProfile } = useUserProfile(hybridToken?.token ?? "");
  const host = getQueryStringParams()?.host?.toString() ?? "";
  const env = getEnvironment();
  const envOverride =
    env !== "production"
      ? getQueryStringParams()?.envOverride?.toString() ??
        getQueryStringParams()?.ihdEnvOverride?.toString()
      : undefined;
  const inLightmile = host === "LIGHTMILE";

  useEffect(() => {
    if (!authToken) {
      return;
    }

    getUserRole(inLightmile)
      .then(setUserRole)
      .catch(() => {
        setUserRole("design");
      });
  }, [authToken, inLightmile]);

  // The PricingPoint Data that comes from the server
  const [unifiedPricingData, setUnifiedPricingData] =
    useState<UnifiedPricingData>();

  // The PricePoints by Financial Product (just cash price point or loan price points)
  const [pricePoints, setPricePoints] = useState<PricePoint[]>();

  // The PricePoints Filtered by all groups
  const [filteredPricePoints, setFilteredPricePoints] =
    useState<PricePoint[]>();

  // The PricePoints Filtered by just the payment groups
  const [
    filteredPricePointsByGroupPayment,
    setFilteredPricePointsByGroupPayment,
  ] = useState<PricePoint[]>();

  const [financialProduct, setFinancialProduct] =
    useState<FinancialProduct>("Cash");

  const [availableFinancialProducts, setAvailableFinancialProducts] = useState<
    FinancialProduct[]
  >([...financialProductsOrderedList]);

  // These are the dynamic fields based on price point rules
  const [dynamicPricingFields, setDynamicPricingFields] =
    useState<ReturnType<typeof getPricingFields>>();

  const [activeFilters, setActiveFilters] = useState<ActiveFilters>([]);
  const [selectedPricePointId, setSelectedPricePointId] =
    useState<PricePoint["id"]>("");
  const [recommendedPricePointId, setRecommendedPricePointId] =
    useState<PricePoint["id"]>("");

  const shouldShowCommission = (inLightmile: boolean, changeOrder: boolean) => {
    if (inLightmile) return true;

    if (!inLightmile && !changeOrder) return false;

    return true;
  };

  const updateFinancialProduct = (financialProduct: FinancialProduct) => {
    setMatchCurrentTerms(false);
    setShowCommission(shouldShowCommission(inLightmile, changeOrder));
    setSelectedPricePointId("");
    setFinancialProduct(financialProduct);
  };

  const [submittingOfferMessage, setSubmittingOfferMessage] =
    useState<string>("");
  const [isLoadingPricePointData, setIsLoadingPricePointData] =
    useState<boolean>();

  const [pricingOffer, setPricingOffer] = useState<PricingOffer>();

  useEffect(() => {
    if (pricingOffer?.opportunity) {
      const { state } = pricingOffer.opportunity;
      setHideSolarRateColumn(
        shouldHideSolarRateColumn(
          state,
          financialProduct,
          changeOrder,
          userProfile,
          { hasHybridAuth, userRole }
        )
      );
    }
  }, [
    pricingOffer,
    changeOrder,
    selectedPricePointId,
    financialProduct,
    userProfile,
    hasHybridAuth,
    userRole,
  ]);

  const [matchCurrentTerms, setMatchCurrentTerms] = useState(
    changeOrder && !repChangeOrder
  );

  const [showCommission, setShowCommission] = useState(
    shouldShowCommission(inLightmile, changeOrder)
  );

  // This is to reset filters if selected items change
  const [resetCounter, setResetCounter] = useState(0);

  const onApplyFilterFn = useCallback(
    (filter: Filters[0], payload: Payload) => {
      setMatchCurrentTerms(false);
      setSelectedPricePointId("");
      let newActiveFilters = uniqBy(
        [
          {
            filter,
            payload,
          },
          ...activeFilters,
        ],
        (filter) => filter.filter.title + filter.filter.type
      );
      if (filter.resetsOtherFilters) {
        setResetCounter(resetCounter + 1);
        newActiveFilters = newActiveFilters.filter(
          (activeFilter) => activeFilter.filter.resetsOtherFilters
        );
      }
      setActiveFilters(newActiveFilters);
    },
    [setResetCounter, activeFilters, resetCounter]
  );

  const updateFilteredPricePoints = useCallback(
    (
      pricePoints: PricePoint[],
      activeFilters: ActiveFilters,
      pricingFields: ReturnType<typeof getPricingFields> | undefined,
      matchCurrentTerms: boolean
    ) => {
      if (!pricePoints) {
        return;
      }

      let pricePointsToFilter = pricePoints;
      if (matchCurrentTerms) {
        const greenChangeOrders = pricePointsToFilter.filter(
          (e) => e.changeOrder?.changeOrderType === "NO_CHANGE_ORDER"
        );
        if (greenChangeOrders.length > 0) {
          pricePointsToFilter = greenChangeOrders;
        } else {
          const yellowChangeOrders = pricePointsToFilter.filter(
            (e) => e.changeOrder?.changeOrderType === "CUSTOMER_CHANGE_ORDER"
          );
          pricePointsToFilter = yellowChangeOrders;
          // If there are no green and no yellow change orders, the list will be blank
        }
      }

      setFilteredPricePoints(
        pricePointsFilter(
          "all groups",
          pricePointsToFilter,
          activeFilters,
          pricingFields?.filters || []
        )
      );
      setFilteredPricePointsByGroupPayment(
        pricePointsFilter(
          "payment group",
          pricePoints,
          activeFilters.filter(
            (filter) => filter.filter.group === Group.Payment
          ),
          (pricingFields?.filters || []).filter(
            (filter) => filter.group === Group.Payment
          )
        )
      );
    },
    []
  );

  useEffect(() => {
    if (!errorData) {
      return;
    }
    onSubmitFailure(errorData.error);
  }, [errorData, onSubmitFailure]);

  useEffect(() => {
    onPricePointIdSelected(selectedPricePointId);
  }, [selectedPricePointId, onPricePointIdSelected]);

  const handleSubmitOfferSuccess = useCallback(
    (
      res: { data?: { submitOffer: { correlationId: string } } | null },
      setMessage = true
    ) => {
      const correlationId = res.data?.submitOffer.correlationId;
      if (correlationId) {
        if (setMessage) {
          setSubmittingOfferMessage(
            "Waiting for your Offer to be Processed..."
          );
        }
        onDomainEvent(correlationId).subscribe((data) => {
          const errors = data.data?.onDomainEvent.errors || [];
          if (errors.length) {
            setErrorData({
              error: new Error(
                `Error Submitting Offer ${JSON.stringify(errors[0])}`
              ),
              action: "Submitting Offer",
            });
          } else {
            onSubmitComplete();
          }
        });
      } else {
        setErrorData({
          error: new Error(
            "The ID to check the progress of the offer was not found."
          ),
          action: "Submitting Offer",
        });
      }
    },
    [onSubmitComplete]
  );

  const handleOfferSubmitFail = useCallback(
    (error: any, retryCount = 0) => {
      const errorMessage = error.message;
      if (errorMessage.includes("Optimistic locking failed")) {
        const pattern = new RegExp(`${offerId} lockVersion (\\d*)`);
        const matchArray = errorMessage.match(pattern); // Returns [<full match>, <capture group 1>]
        if (matchArray && matchArray.length > 0) {
          const newLockVersion = matchArray[1];
          if (newLockVersion && newLockVersion.length > 0) {
            if (retryCount > 0) {
              setErrorData({
                error: new Error(
                  "Error creating offer Reprocessing the request failed to correct the lockVersion"
                ),
                action: "Submitting Offer",
              });
            } else {
              setSubmittingOfferMessage(
                "There was an issue during the initial submission, we are automatically retrying."
              );
              submitOffer(
                offerId,
                selectedPricePointId,
                Number(newLockVersion),
                host,
                offerEvSpan
              ).then(
                (res) => handleSubmitOfferSuccess(res, false),
                (error) => handleOfferSubmitFail(error, retryCount + 1)
              );
            }
            return;
          }
        }
      }

      setErrorData({
        error: new Error("Error creating offer", { cause: error }),
        action: "Submitting Offer",
      });
    },
    [handleSubmitOfferSuccess, host, offerEvSpan, offerId, selectedPricePointId]
  );

  useEffect(() => {
    if (!submittingOffer) {
      return;
    }
    if (pricingOffer?.lockVersion === undefined) {
      setErrorData({
        error: new Error(
          "Error while submitting offer: Lock Version has not been set."
        ),
        action: "Submitting Offer",
      });
      return;
    }

    if (!authToken) {
      setErrorData({
        error: new Error(
          "Error while submitting offer: Missing cognito token."
        ),
        action: "Submitting Offer",
      });
      return;
    }

    setSubmittingOfferMessage("Submitting Offer...");
    Mixpanel.track(OFFER_CREATION__SUBMIT_OFFER, {
      offerId,
      bundleId: pricingOffer?.bundleId,
      inLightmile,
    });

    getStoreFrontDesignImagesWithAuthRetry(
      cognitoToken ?? "",
      pricingOffer?.designId,
      pricingOffer?.designVersion,
      envOverride
    )
      .then((designImageUrls) => {
        if (lightmileProjectId && authToken && designImageUrls) {
          return transferDesignImages(authToken, lightmileProjectId, {
            proposalLargeUrl: designImageUrls.proposalLarge?.url,
            proposalSmallUrl: designImageUrls.proposalSmall?.url,
            pinsUrl: designImageUrls.pins?.url,
          });
        }
      })
      .then(() =>
        submitOffer(
          offerId,
          selectedPricePointId,
          pricingOffer.lockVersion,
          host,
          offerEvSpan
        )
      )
      .then(handleSubmitOfferSuccess, handleOfferSubmitFail);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submittingOffer]);

  useEffect(() => {
    if (
      !matchCurrentTerms ||
      !dynamicPricingFields?.filters ||
      !pricingOffer?.existingContract
    ) {
      return;
    }

    setFinancialProduct(pricingOffer.existingContract.type);
    const newFilters = dynamicPricingFields.filters
      .filter(
        (e) =>
          e.currentContractValue &&
          e.currentContractValue(pricingOffer.existingContract!) !== undefined
      )
      .map((filter) => {
        const value = filter.currentContractValue!(
          pricingOffer.existingContract!
        );
        return {
          filter,
          payload: {
            text: filter.displayFilter(value),
            value,
          },
        };
      });
    setActiveFilters(newFilters);
  }, [dynamicPricingFields, matchCurrentTerms, pricingOffer]);

  useEffect(() => {
    if (!pricePoints) {
      return;
    }

    updateFilteredPricePoints(
      pricePoints,
      activeFilters,
      dynamicPricingFields,
      matchCurrentTerms
    );
  }, [
    pricePoints,
    activeFilters,
    dynamicPricingFields,
    matchCurrentTerms,
    updateFilteredPricePoints,
  ]);

  const isFusionExpertUser = (
    salesDivision: SalesDivision | undefined,
    userProfile: Pick<UserProfile, "isFusion" | "sellsSunrun"> | undefined
  ): boolean => {
    return (
      salesDivision === FUSION &&
      userProfile?.isFusion === true &&
      userProfile?.sellsSunrun === "Yes"
    );
  };

  // Gets data
  useEffect(() => {
    if (!authToken || !userRole || isLoadingPricePointData) {
      return;
    }
    if (pricePoints) {
      return;
    }
    if (errorData) {
      return;
    }

    setIsLoadingPricePointData(true);
    getPricingOffer(authToken.token, offerId)
      .then((data) => {
        setPricingOffer(data);
        return getPricePoints(offerId, userRole)
          .then((unifiedPricingData) => {
            setUnifiedPricingData(unifiedPricingData);
            const filteredAvailableFinancialProducts =
              financialProductsOrderedList.filter(
                (product) =>
                  unifiedPricingData[
                    camelCase(product) as CamelCase<FinancialProduct>
                  ]?.length
              );
            setAvailableFinancialProducts(filteredAvailableFinancialProducts);

            let availableFinancialProduct: FinancialProduct;

            if (changeOrder && data.existingContract?.type) {
              availableFinancialProduct = data.existingContract.type;
            } else if (unifiedPricingData.preferredPricingType) {
              availableFinancialProduct =
                unifiedPricingData.preferredPricingType;
            } else {
              availableFinancialProduct =
                getDefaultFinancialProduct(
                  filteredAvailableFinancialProducts
                ) ?? filteredAvailableFinancialProducts[0];
            }
            setFinancialProduct(availableFinancialProduct);
            const financialProductCamelCase = camelCase(
              availableFinancialProduct
            ) as CamelCase<FinancialProduct>;

            const pricingFields = getPricingFields({
              customerState: data.opportunity.state,
              customerUtility: data.opportunity.utility,
              salesDivision: data.opportunity.salesDivision,
              bundleId: data.bundleId,
              unifiedPricingData,
              financialProduct: availableFinancialProduct,
              changeOrder,
              showCommission,
              isFusionExpert: isFusionExpertUser(
                data.opportunity.salesDivision,
                userProfile
              ),
              offerLines:
                data.lines?.map((line) => ({
                  id: line.id,
                  productId: line.variant.product.id,
                })) ?? [],
            });
            if (!pricingFields) {
              reportPricingUnavailable();
              setIsLoadingPricePointData(false);
              return;
            }
            setDynamicPricingFields(pricingFields);

            const currentPricePoints =
              unifiedPricingData[financialProductCamelCase];
            setPricePoints(currentPricePoints);

            if (changeOrder && data?.existingContract) {
              const pricePoint = currentPricePoints.find(
                (e) => e.changeOrder?.recommended === true
              )?.id;
              if (pricePoint) {
                setRecommendedPricePointId(pricePoint);
                setSelectedPricePointId(pricePoint);
              }
            } else if (unifiedPricingData.preferredPricePointId) {
              setRecommendedPricePointId(
                unifiedPricingData.preferredPricePointId
              );
              setSelectedPricePointId(unifiedPricingData.preferredPricePointId);
            } else {
              setRecommendedPricePointId("");
            }

            setIsLoadingPricePointData(false);
          })
          .catch((error) => {
            setIsLoadingPricePointData(false);
            if (
              setChangeOrderError &&
              (error?.message?.includes(
                "ERROR: Lambda is initializing your function."
              ) ||
                error?.message?.includes("Execution timed out"))
            ) {
              setChangeOrderError(error);
              return;
            }
            setErrorData({
              error: new Error("There was a problem fetching pricing.", {
                cause: error,
              }),
              action: "Get Pricing",
            });
          });
      })
      .catch((error) => {
        setIsLoadingPricePointData(false);
        setErrorData({
          error: new Error("There was a problem fetching the offer.", {
            cause: error,
          }),
          action: "Get Pricing",
        });
      });
  }, [
    offerId,
    authToken,
    userRole,
    activeFilters,
    updateFilteredPricePoints,
    pricePoints,
    isLoadingPricePointData,
    errorData,
    changeOrder,
    showCommission,
    setChangeOrderError,
    userProfile,
  ]);

  useEffect(() => {
    setActiveFilters([]);

    if (unifiedPricingData && pricingOffer?.opportunity) {
      const pricingFields = getPricingFields({
        customerState: pricingOffer.opportunity.state,
        customerUtility: pricingOffer.opportunity.utility,
        salesDivision: pricingOffer.opportunity.salesDivision,
        bundleId: pricingOffer.bundleId,
        unifiedPricingData,
        financialProduct,
        changeOrder,
        showCommission,
        isFusionExpert: isFusionExpertUser(
          pricingOffer.opportunity.salesDivision,
          userProfile
        ),
        offerLines:
          pricingOffer.lines?.map((line) => ({
            id: line.id,
            productId: line.variant.product.id,
          })) ?? [],
      });
      if (!pricingFields) {
        reportPricingUnavailable();
        return;
      }
      setDynamicPricingFields(pricingFields);
      setPricePoints(
        unifiedPricingData[
          camelCase(financialProduct) as CamelCase<FinancialProduct>
        ]
      );
    }
  }, [
    unifiedPricingData,
    financialProduct,
    pricingOffer,
    changeOrder,
    showCommission,
    userProfile,
  ]);

  return (
    <>
      {!errorData && submittingOffer && (
        <LoadingContainer>
          <LoadingOverlay message={submittingOfferMessage} />
        </LoadingContainer>
      )}
      {!errorData && !unifiedPricingData && (
        <LoadingContainer data-testid="getting-pricing-overlay">
          <LoadingOverlay
            message="Getting pricing..."
            data-testid="getting-pricing-overlay"
          />
        </LoadingContainer>
      )}
      {errorData && (
        <Modal
          onClose={() => setErrorData(undefined)}
          testId="pricing-page-error-modal"
        >
          <ErrorComponent
            context="PricingPage"
            action={errorData.action}
            error={errorData.error}
            severity={errorData.severity}
          ></ErrorComponent>
        </Modal>
      )}
      {!submittingOffer && unifiedPricingData && (
        <PricingPageContainer>
          <ControlsArea>
            {pricingOffer?.existingContract && (
              <CurrentContractTerms
                contractTerms={pricingOffer.existingContract}
              />
            )}
            {repChangeOrder && <CurrentHomeUpgradeSection />}
            <PricingPayment
              resetCounter={resetCounter}
              availableFinancialProducts={availableFinancialProducts}
              selectedFinancialProduct={financialProduct}
              onFinancialProductChange={updateFinancialProduct}
              paymentOptions={dynamicPricingFields?.paymentOptions || []}
              filters={(dynamicPricingFields?.filters || []).filter(
                (pf) => pf.group === Group.Payment
              )}
              activeFilters={activeFilters}
              pricePoints={pricePoints || []}
              onApplyFilter={onApplyFilterFn}
            />
          </ControlsArea>
          <ResultsArea>
            {dynamicPricingFields && dynamicPricingFields.columns && (
              <PricingOptions
                resetCounter={resetCounter}
                financialProduct={financialProduct}
                onApplyFilter={onApplyFilterFn}
                paymentPricePoints={filteredPricePointsByGroupPayment || []}
                filteredPricePoints={filteredPricePoints || []}
                columns={dynamicPricingFields?.columns || []}
                filters={(dynamicPricingFields?.filters || []).filter(
                  (pf) => pf.group === Group.PricingOption
                )}
                activeFilters={activeFilters}
                onPricePointSelected={setSelectedPricePointId}
                pricePointSelected={selectedPricePointId}
                inLightmile={inLightmile}
                changeOrder={changeOrder}
                matchCurrentTerms={matchCurrentTerms}
                setMatchCurrentTerms={setMatchCurrentTerms}
                showCommission={showCommission}
                setShowCommission={setShowCommission}
                recommendedPricePointId={recommendedPricePointId}
                repChangeOrder={repChangeOrder}
                hideSolarRateColumn={hideSolarRateColumn}
                bundleId={pricingOffer?.bundleId}
              />
            )}
          </ResultsArea>
        </PricingPageContainer>
      )}
    </>
  );
};

const PricingPageContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 25px;
  font-family: "Roobert";

  @media (min-width: 1200px) {
    flex-direction: row;
    align-items: flex-start;
  }
`;
const ResultsArea = styled.div`
  flex: 2;
  min-width: 0;
`;
const ControlsArea = styled.div`
  > * + * {
    margin-top: 1.5rem;
  }

  flex: 1;
  @media (min-width: 1200px) {
    position: sticky;
    top: 0;
    flex: 0 0 240px;
    max-width: 240px;
    /* Header (8rem + 64px padding) and sticky bottom action bar (116px) */
    max-height: calc(100vh - 8rem - 64px - 116px);
    overflow-y: auto;
    overscroll-behavior: contain;
  }
`;

const LoadingContainer = styled.div`
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100vw;
  height: 100vh;
  z-index: 1000;

  background: linear-gradient(
    180deg,
    ${tokens.CHARGEBLUE_50} 0,
    ${tokens.BACKGROUND_LIGHT} 16rem
  );
`;

export { PricingPage };
